import { ComponentPropsWithRef, ElementType, HTMLAttributes, ReactNode } from "react";

import { cn } from "./cn";

// 바탕 위 흰 블록 — 테두리 한 줄, 그림자 없음. 머리줄은 title·description·actions 가 있을 때만 그린다.
const PADDING_CLASS = {
  none: "",
  sm: "p-3",
  md: "p-4",
  lg: "p-6",
} as const;

type CardTag = "section" | "div" | "article" | "aside" | "li" | "ul" | "form" | "details" | "p";

interface CardClassOptions {
  padding?: keyof typeof PADDING_CLASS;
  interactive?: boolean;
}

// 컴포넌트로 감싸기 어려운 카드(next/link·a)용 클래스 — SectionCard 도 이 한 벌을 쓴다. interactive 는 누르는 카드의 hover 바탕.
export const sectionCardClass = ({ padding = "md", interactive = false }: CardClassOptions = {}) =>
  cn("rounded-lg border border-border bg-card", PADDING_CLASS[padding], interactive && "hover:bg-muted");

// p·details 는 머리줄(div·h2)을 담을 수 없는 자리라 title·description·actions 를 타입에서 막는다.
type HeaderProps<T extends CardTag> = T extends "p" | "details"
  ? { title?: never; description?: never; actions?: never }
  : { title?: ReactNode; description?: ReactNode; actions?: ReactNode };

type Props<T extends CardTag> = Omit<ComponentPropsWithRef<T>, "title" | "children" | "className"> &
  CardClassOptions &
  HeaderProps<T> & {
    children: ReactNode;
    className?: string;
    as?: T;
  };

const SectionCard = <T extends CardTag = "section">({
  title,
  description,
  actions,
  children,
  padding,
  interactive,
  as,
  className,
  ...rest
}: Props<T>) => {
  const Tag: ElementType = as ?? "section";
  // 태그별 props 검사는 Props<T> 가 호출하는 쪽에서 끝냈다 — 여기서는 태그 합집합이라 한 타입으로 넘긴다.
  const attrs = rest as HTMLAttributes<HTMLElement>;
  return (
    <Tag className={cn(sectionCardClass({ padding, interactive }), className)} {...attrs}>
      {(title || description || actions) && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {(title || description) && (
            <div>
              {title && <h2 className="text-base font-semibold">{title}</h2>}
              {description && <p className="text-xs text-muted-foreground">{description}</p>}
            </div>
          )}
          {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </Tag>
  );
};

export default SectionCard;
